import React, { useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { Button } from "./ui/button";
import SearchBar2 from "./SearchBar2";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { FlagIcon } from "react-flag-kit";

const Header = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Language options with flag codes for react-flag-kit
  const languages = [
    { code: "en", label: t("EN"), flagCode: "GB" }, // United Kingdom flag
    { code: "ar", label: t("AR"), flagCode: "EG" }, // Egypt flag (for Arabic)
    { code: "es", label: t("ES"), flagCode: "ES" }, // Spain flag
  ];

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    setIsAuthenticated(!!token);
  }, []);

  const handleLogout = async () => {
    try {
      // Use environment variable for API base URL
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/admin/logout`);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminRole");
      setIsAuthenticated(false);
      navigate("/admin");
      window.location.reload();
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode.toLowerCase());
  };

  const isRTL = i18n.language === "ar";

  return (
    <header
      dir={isRTL ? "rtl" : "ltr"}
      className={`my-[15px] py-3 flex flex-col justify-between items-center px-4 border-b w-full bg-white transition-all duration-300 sm:px-6 lg:px-8 ${
        isRTL ? "min-[1400px]:pr-[288px]" : "min-[1400px]:pl-[288px]"
      }`}
    >
      <div className="flex flex-col sm:flex-row justify-between items-center w-full max-w-7xl mx-auto gap-4 sm:gap-0">
        {isAuthenticated ? (
          <>
            {/* Admin Dashboard Title */}
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-black">
              {t("header.adminDashboard")}
            </h1>
            {/* Language and Logout Buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              <div className="flex rounded-lg overflow-hidden border">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`px-3 py-2 text-sm transition-colors duration-200 flex items-center gap-2 ${
                      lang.code === i18n.language
                        ? "bg-gray-100 font-medium"
                        : "bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => changeLanguage(lang.code)}
                    aria-label={t("header.changeLanguage", {
                      lang: lang.label,
                    })}
                  >
                    <FlagIcon code={lang.flagCode} size={16} className="inline-block" />
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
              <Button
                className="bg-[#F59E0B] text-white flex items-center gap-2 px-4 py-2 w-full sm:w-auto"
                onClick={handleLogout}
              >
                <LogOut size={16} className="inline-block" />
                {t("header.logout")}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Search Bar for Non-Authenticated Users */}
            <div className="w-full sm:w-auto flex-grow">
              <SearchBar2 />
            </div>
            {/* Donation Button and Language Buttons */}
            <div className="flex items-center gap-3 sm:gap-4">
              <Button
                className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 px-4 py-2 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={() => navigate("/support")}
              >
                Donate
              </Button>
              <div className="flex rounded-lg overflow-hidden border">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`px-3 py-2 text-sm transition-colors duration-200 flex items-center gap-2 ${
                      lang.code === i18n.language
                        ? "bg-gray-100 font-medium"
                        : "bg-white hover:bg-gray-50"
                    }`}
                    onClick={() => changeLanguage(lang.code)}
                    aria-label={t("header.changeLanguage", {
                      lang: lang.label,
                    })}
                  >
                    <FlagIcon code={lang.flagCode} size={16} className="inline-block" />
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;